/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: Layout.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Main layout component that provides the overall application structure
    with sidebar, header, and content area for the Ring2All Platform.

 🔧 Notes:
    - Implements main application layout with sidebar and header
    - Integrates with theme store for dynamic styling
    - Provides responsive layout with proper spacing and positioning
    - Supports sidebar collapse/expand functionality
    - Manages main content area and navigation structure
    - Ensures consistent layout across all application pages

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { ReactNode, useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar/Sidebar';
import Header from './Header';
import { SidebarProvider, useSidebarContext } from '../contexts/SidebarContext';
import { WebRTCPhonePanel } from './webrtc/WebRTCPhonePanel';
import { useAuthStore } from '../store/auth';
import { AiAssistantWidget } from './ai/AiAssistantWidget';
import { AiChatWindow } from './ai/AiChatWindow';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={200} skipDelayDuration={100}>
      <SidebarProvider>
        <LayoutContent>{children}</LayoutContent>
      </SidebarProvider>
    </TooltipPrimitive.Provider>
  );
}

function LayoutContent({ children }: LayoutProps) {
  const { isCollapsed, setIsCollapsed } = useSidebarContext();
  const { user } = useAuthStore();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const SIDEBAR_WIDTH = 350;
  const SIDEBAR_COLLAPSED_WIDTH = 80;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const effectiveSidebarWidth = isMobile ? 0 : (isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);

  // WebRTC Phone panel state
  const [isPhonePanelOpen, setIsPhonePanelOpen] = useState(false);

  // Check if user has softphone enabled
  const showSoftphone = user?.softphoneEnabled && user?.extensionNumber;

  // Reset scroll to top when route changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      // Reset scroll immediately when route changes
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }

      // Also reset window scroll as fallback (in case content overflows)
      window.scrollTo(0, 0);

      // Force reset again after a short delay to handle async content
      setTimeout(() => {
        if (mainRef.current) {
          mainRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      }, 100);
    });
  }, [location.pathname]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <Header />
      {isMobile && !isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-[999] transition-opacity duration-300"
          onClick={() => setIsCollapsed(true)}
        />
      )}
      <Sidebar />
      <main
        ref={mainRef}
        className="fixed overflow-x-hidden overflow-y-auto transition-all duration-300 bg-background"
        style={{
          top: '80px',
          left: `${effectiveSidebarWidth}px`,
          right: isPhonePanelOpen && showSoftphone ? '320px' : '0',
          bottom: '0',
        }}
      >
        <div className="px-3 sm:px-6 pt-1 pb-6">
          {children}
        </div>
      </main>
      {/* WebRTC Phone Panel - only shown if user has softphone enabled */}
      {showSoftphone && (
        <WebRTCPhonePanel
          isOpen={isPhonePanelOpen}
          onToggle={() => setIsPhonePanelOpen(!isPhonePanelOpen)}
        />
      )}

      {/* AI Assistant Widgets */}
      {user?.chatbotEnabled && (
        <>
          <AiAssistantWidget />
          <AiChatWindow />
        </>
      )}
    </div>
  );
}
